// Module Exploration : lecture du graphe d'un projet côté interface (index, libellés, états).
//
// Le serveur évalue le projet (arbre des fonctions, 2026-10-14, refait le 2026-10-22 : des branches rangées les unes
// sous les autres, dont les sous-branches sont des composants — tous nécessaires — ; un choix est une branche dont les
// sous-branches sont des alternatives — une seule retenue, l'étoile — ; chaque branche porte ses verrous ; la solution
// en cours suit les étoiles, les solutions enregistrées en gardent une copie) ; l'interface reçoit les éléments, les
// liens, les résultats et l'évaluation, et en tire des index pour chaque vue. Rien n'est recalculé ici sauf la
// navigation.

export const TYPES = {
  attribute: { label: 'Attribut', plural: 'Attributs', code: 'AT', tone: 'violet' },
  candidate: { label: 'Branche', plural: 'Branches', code: 'B', tone: 'blue' },
  lock: { label: 'Verrou', plural: 'Verrous', code: 'V', tone: 'amber' },
  artefact: { label: 'Artefact', plural: 'Artefacts', code: 'A', tone: 'indigo' },
  configuration: { label: 'Solution', plural: 'Solutions', code: 'S', tone: 'teal' },
  decision: { label: 'Décision', plural: 'Décisions', code: 'D', tone: 'slate' },
  knowledge: { label: 'Connaissance', plural: 'Connaissances', code: 'KN', tone: 'emerald' },
  concept: { label: 'Concept', plural: 'Concepts', code: 'CO', tone: 'sky' },
  block: { label: 'Bloc', plural: 'Blocs', code: 'BL', tone: 'slate' },
  function: { label: 'Fonction', plural: 'Fonctions', code: 'F', tone: 'slate' },
  interaction: { label: 'Interaction', plural: 'Interactions', code: 'I', tone: 'slate' },
};

export const IMPORTANCE = { essentiel: 'Essentiel', important: 'Important', souhaitable: 'Souhaitable' };
export const IMPORTANCE_WEIGHT = { essentiel: 3, important: 2, souhaitable: 1 };
export const FIRMNESS = { ferme: 'Ferme', negociable: 'Négociable', vise: 'Visé' };
export const UNCERTAINTY = { forte: 'Forte', moyenne: 'Moyenne', faible: 'Faible' };
export const LOCK_TYPES = {
  technique: { label: 'Technique', hint: 'Menace la cible d’un attribut' },
  realisation: { label: 'Réalisation', hint: 'Problème de mise en œuvre d’une branche' },
  integration: { label: 'Intégration', hint: 'Ne se pose que si des branches sont combinées (solution)' },
};
/** Ce que sont les sous-branches d'une branche : ses composants, ou (pour un choix) des alternatives. */
export const CHILDREN = {
  parties: { label: 'Composants', hint: 'tous nécessaires', add: 'Sous-branche' },
  variantes: { label: 'Alternatives', hint: 'une au choix', add: 'Alternative' },
};
export const LEVELS = {
  avis: { label: 'Avis', short: 'Av', rank: 0.5, hint: 'Retour d’expert, de client, jugement' },
  theorique: { label: 'Théorique', short: 'T', rank: 1, hint: 'Calcul, note, état de l’art' },
  simulation: { label: 'Simulation', short: 'S', rank: 2, hint: 'Modèle, simulation, jumeau numérique' },
  prototype: { label: 'Prototype', short: 'P', rank: 3, hint: 'Essai physique, banc, maquette' },
};
export const NATURES = {
  note_calcul: { label: 'Note de calcul', level: 'theorique' },
  simulation: { label: 'Simulation', level: 'simulation' },
  jumeau_numerique: { label: 'Jumeau numérique', level: 'simulation' },
  prototype: { label: 'Prototype', level: 'prototype' },
  banc: { label: 'Banc d’essai', level: 'prototype' },
  essai_terrain: { label: 'Essai terrain', level: 'prototype' },
  echantillon: { label: 'Échantillon', level: 'prototype' },
  cao: { label: 'CAO', level: 'theorique' },
  code: { label: 'Code', level: 'simulation' },
  avis_expert: { label: 'Avis d’expert', level: 'avis' },
  document: { label: 'Document', level: 'theorique' },
  autre: { label: 'Autre', level: 'theorique' },
};
export const ARTEFACT_STATUS = {
  planifie: { label: 'Planifié', tone: 'slate' },
  en_cours: { label: 'En cours', tone: 'sky' },
  termine: { label: 'Terminé', tone: 'emerald' },
  annule: { label: 'Annulé', tone: 'zinc' },
};
export const VERDICTS = {
  positif: { label: 'Positif', tone: 'emerald' },
  mitige: { label: 'Mitigé', tone: 'amber' },
  negatif: { label: 'Négatif', tone: 'rose' },
  non_concluant: { label: 'Non concluant', tone: 'slate' },
};
export const CONFIDENCE = { faible: 'Faible', moyenne: 'Moyenne', forte: 'Forte' };
export const CONFIG_STATUS = { etude: 'À l’étude', retenue: 'Retenue', ecartee: 'Écartée' };
export const DECISION_STATUS = { proposee: 'Proposée', validee: 'Validée', rejetee: 'Rejetée' };
export const DECISION_EFFECTS = {
  integrate: 'Intégrer la branche',
  abandon: 'Abandonner la branche',
  sleep: 'Mettre en sommeil',
  reopen: 'Rouvrir la branche',
  retain_configuration: 'Retenir la solution',
  drop_configuration: 'Écarter la solution',
  dismiss_lock: 'Écarter le verrou',
  reopen_lock: 'Rouvrir le verrou',
  change_target: 'Réviser la cible',
  note: 'Décision sans effet sur le graphe',
};
export const KNOWLEDGE_NATURE = { acquis: 'Acquis', refutation: 'Réfutation', savoir_faire: 'Savoir-faire' };
export const PERIMETERS = {
  interne: { label: 'Interne', hint: 'Conçu par nous' },
  partenaire: { label: 'Partenaire', hint: 'Conçu par un tiers dans le même système' },
  externe: { label: 'Externe', hint: 'Milieu extérieur : on le caractérise' },
};
export const BLOCK_STATUS = {
  realise: 'Réalisé', en_exploration: 'En exploration', non_realise: 'Non réalisé', caracterise: 'Caractérisé',
  a_caracteriser: 'À caractériser',
};
export const FUNCTION_STATUS = { attendue: 'Attendue', demontree: 'Démontrée', insuffisante: 'Insuffisante' };

export const OPERATORS = [
  { value: '>=', label: '≥' }, { value: '>', label: '>' }, { value: '<=', label: '≤' }, { value: '<', label: '<' },
  { value: '=', label: '=' }, { value: 'entre', label: 'entre' },
];

const fmt = (v) => (typeof v === 'number' ? String(v).replace('.', ',') : String(v ?? ''));

/** La question d'un verrou, quand elle dit autre chose que son titre (titre court + question : 2026-10-09). */
export const lockQuestion = (node) => {
  const q = (node?.props?.question || '').trim();
  const bare = (t) => (t || '').trim().toLowerCase().replace(/[\s?]+$/, '');
  return q && bare(q) !== bare(node.title) ? q : null;
};

/** Ce qu'on lit d'un verrou en un coup d'œil : son titre court, sinon sa question. */
export const lockLabel = (node) => (node?.title || '').trim() || (node?.props?.question || '').trim();

export const targetText = (t) => {
  if (!t) return '';
  const unit = t.unit ? ` ${t.unit}` : '';
  if (t.op === 'entre') return `entre ${fmt(t.value)} et ${fmt(t.value_max)}${unit}`;
  const symbol = { '<': '<', '<=': '≤', '>': '>', '>=': '≥', '=': '=' }[t.op] || t.op;
  return `${symbol} ${fmt(t.value)}${unit}`;
};

export const measureText = (r) => {
  if (!r) return '';
  if (r.measure_value == null) return r.value_text || '';
  let text = fmt(r.measure_value);
  if (r.measure_uncertainty) text += ` ± ${fmt(r.measure_uncertainty)}`;
  return text + (r.measure_unit ? ` ${r.measure_unit}` : '');
};

// ── Index ─────────────────────────────────────────────────────────────────────────────────────────────────────
export function buildIndex(bundle) {
  const nodes = bundle?.nodes || [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const byType = {};
  Object.keys(TYPES).forEach((t) => { byType[t] = []; });
  nodes.forEach((n) => { (byType[n.type] = byType[n.type] || []).push(n); });
  Object.values(byType).forEach((list) => list.sort((a, b) => a.num - b.num));
  const out = new Map();
  const inn = new Map();
  const push = (map, key, value) => { const list = map.get(key); if (list) list.push(value); else map.set(key, [value]); };
  [...(bundle?.links || [])].sort((a, b) => a.id - b.id).forEach((l) => {
    push(out, `${l.src}|${l.type}`, l.dst);
    push(inn, `${l.dst}|${l.type}`, l.src);
  });
  const resultsByArtefact = new Map();
  const resultsByLock = new Map();
  (bundle?.results || []).forEach((r) => { push(resultsByArtefact, r.artefact_id, r); push(resultsByLock, r.lock_id, r); });
  const filesByNode = new Map();
  (bundle?.files || []).forEach((f) => push(filesByNode, f.node_id, f));
  const codes = new Map(nodes.map((n) => [n.code, n.id]));
  const ev = bundle?.evaluation || {};
  const ix = {
    nodes, byId, byType, codes, ev,
    alerts: bundle?.alerts || {},
    out: (id, type) => out.get(`${id}|${type}`) || [],
    inn: (id, type) => inn.get(`${id}|${type}`) || [],
    has: (type, src, dst) => (out.get(`${src}|${type}`) || []).includes(dst),
    resultsOf: (artefactId) => resultsByArtefact.get(artefactId) || [],
    resultsOn: (lockId) => resultsByLock.get(lockId) || [],
    filesOf: (nodeId) => filesByNode.get(nodeId) || [],
    code: (id) => byId.get(id)?.code || '?',
    title: (id) => byId.get(id)?.title || '',
  };
  ix.isCandidateOut = (c) => !!c?.props?.exit && c.props.exit !== 'integre';
  ix.carriers = (lockId) => ix.inn(lockId, 'carries');
  ix.carrier = (lockId) => ix.inn(lockId, 'carries')[0] ?? null;
  ix.locksOf = (candidateId) => ix.out(candidateId, 'carries');
  ix.kept = (conf) => (conf?.props?.candidate_ids || []).filter((id) => byId.has(id));
  // Arbre des fonctions : la branche au-dessus (null : dossier de base), les sous-branches dans l'ordre.
  const children = new Map();
  ix.parentOf = (id) => {
    const parent = byId.get(id)?.props?.parent_id;
    return parent != null && parent !== id && byId.get(parent)?.type === 'candidate' ? parent : null;
  };
  [...byType.candidate].sort((a, b) => (a.props?.order || 0) - (b.props?.order || 0) || a.num - b.num)
    .forEach((c) => push(children, ix.parentOf(c.id), c.id));
  ix.childrenOf = (id) => children.get(id ?? null) || [];
  ix.roots = () => ix.childrenOf(null);
  ix.isRoot = (id) => ix.parentOf(id) == null;
  ix.ancestors = (id) => {
    const chain = [];
    const seen = new Set([id]);
    let parent = ix.parentOf(id);
    while (parent != null && !seen.has(parent)) { seen.add(parent); chain.unshift(parent); parent = ix.parentOf(parent); }
    return chain;
  };
  ix.descendants = (id) => {
    const found = [];
    const seen = new Set([id]);
    const walk = (x) => ix.childrenOf(x).forEach((k) => { if (!seen.has(k)) { seen.add(k); found.push(k); walk(k); } });
    walk(id);
    return found;
  };
  ix.mode = (id) => (byId.get(id)?.props?.children === 'variantes' ? 'variantes' : 'parties');
  /** Un choix : ses sous-branches sont des alternatives (une seule retenue). */
  ix.isChoice = (id) => byId.get(id)?.type === 'candidate' && ix.mode(id) === 'variantes';
  /** Une alternative (sa branche au-dessus est un choix) ? */
  ix.isVariant = (id) => ix.parentOf(id) != null && ix.mode(ix.parentOf(id)) === 'variantes';
  ix.isAlternative = ix.isVariant;
  /** L'alternative retenue d'un choix (l'étoile), si elle y est toujours. */
  ix.pickedOf = (choiceId) => {
    const picked = byId.get(choiceId)?.props?.picked_id;
    return picked != null && ix.parentOf(picked) === choiceId ? picked : null;
  };
  /** Le nom d'un choix : son titre, sinon ses alternatives (« Capteur à effet Hall ou Capteur ultrason »). */
  ix.choiceName = (id) => (byId.get(id)?.title || '').trim()
    || ix.childrenOf(id).map((k) => byId.get(k)?.title || 'sans titre').join(' ou ') || 'Choix';
  /** Nom d'une branche à l'affichage (un choix sans titre prend le nom de ses alternatives). */
  ix.label = (id) => (ix.isChoice(id) ? ix.choiceName(id) : (byId.get(id)?.title || 'sans titre'));
  /** « Vérin effet Hall › Capteur › Hall 3D » */
  ix.path = (id, sep = ' › ') => [...ix.ancestors(id), id].map((x) => ix.label(x)).join(sep);
  /** Les branches dans l'ordre de lecture de l'arbre : [{id, depth}]. */
  ix.treeOrder = () => {
    const order = [];
    const seen = new Set();
    const walk = (id, depth) => ix.childrenOf(id).forEach((k) => {
      if (seen.has(k)) return;
      seen.add(k);
      order.push({ id: k, depth });
      walk(k, depth + 1);
    });
    walk(null, 0);
    byType.candidate.forEach((c) => { if (!seen.has(c.id)) order.push({ id: c.id, depth: 0 }); });
    return order;
  };
  ix.lockState = (id) => ev.locks?.[String(id)];
  ix.candidateState = (id) => ev.candidates?.[String(id)];
  ix.attributeState = (id) => ev.needs?.[String(id)];
  ix.scenario = (id) => ev.scenarios?.[String(id)];
  /** Solution en cours d'un dossier de base (les étoiles), s'il a au moins un choix. */
  ix.current = (rootId) => ev.current?.[String(rootId)];
  // Paramètres (2026-10-22) : ceux d'une branche, ses valeurs retenues, sa grille ; un paramètre par sa clé.
  ix.paramsOf = (id) => byId.get(id)?.props?.parameters || [];
  ix.pointOf = (id) => byId.get(id)?.props?.point || {};
  ix.grid = (id) => ev.grids?.[String(id)];
  ix.solutionPoint = ev.point || {};
  const paramByKey = new Map();
  byType.candidate.forEach((c) => (c.props?.parameters || []).forEach((p) => paramByKey.set(p.key, { ...p, branchId: c.id })));
  ix.param = (key) => paramByKey.get(key) || null;
  ix.criticality = (id) => ev.criticality?.[String(id)];
  ix.integration = (lock) => lock?.props?.lock_type === 'integration' && ix.carriers(lock.id).length > 1;
  // Programme : liens « suite de » avec les autres projets, pistes qui ressemblent à une piste sortie ailleurs.
  ix.programme = bundle?.programme || null;
  ix.lineage = (id) => ix.programme?.continuations?.[String(id)] || [];
  ix.echoes = (id) => ix.programme?.echoes?.[String(id)] || [];
  return ix;
}

/** « suite de C2 · Vérin NG » : d'où vient un élément repris d'un projet précédent du programme. */
export const lineageText = (items) => items.filter((i) => i.direction === 'from')
  .map((i) => `suite de ${i.node.code} · ${i.node.project_name}`).join(' ; ');

/** La solution en cours d'un dossier de base : lui, et à chaque choix rencontré l'alternative retenue (on ne descend
 * que par les étoiles). C'est ce qu'enregistre « Enregistrer comme solution ». */
export function currentPicks(ix, rootId) {
  const ids = [rootId];
  const seen = new Set();
  const live = (id) => ix.childrenOf(id).filter((k) => !ix.isCandidateOut(ix.byId.get(k)));
  const walk = (id) => {
    if (seen.has(id)) return;
    seen.add(id);
    if (ix.isChoice(id)) {
      const picked = ix.pickedOf(id);
      if (picked != null && live(id).includes(picked)) { ids.push(picked); walk(picked); }
      return;
    }
    live(id).forEach(walk);
  };
  walk(rootId);
  return ids;
}

/** Les branches comprises dans la solution en cours d'un dossier de base (on ne descend que par les étoiles). */
export function currentClosure(ix, rootId) {
  const inside = new Set();
  const live = (id) => ix.childrenOf(id).filter((k) => !ix.isCandidateOut(ix.byId.get(k)));
  const walk = (id) => {
    if (inside.has(id)) return;
    inside.add(id);
    if (ix.isChoice(id)) {
      const picked = ix.pickedOf(id);
      if (picked != null && live(id).includes(picked)) walk(picked);
      return;
    }
    live(id).forEach(walk);
  };
  walk(rootId);
  return inside;
}

/** Valeurs retenues des paramètres de la solution en cours d'un dossier de base. */
export function currentPoint(ix, rootId) {
  const point = {};
  currentClosure(ix, rootId).forEach((id) => { if (ix.paramsOf(id).length) Object.assign(point, ix.pointOf(id)); });
  return point;
}

/** Retenir des valeurs sur une branche, c'est la choisir : les étoiles à poser pour qu'elle soit dans la solution en
 * cours (elle, et au-dessus, à chaque choix traversé). [{op: 'pick'}…] et leur annulation. */
export function starPath(ix, id) {
  const ops = [];
  const undo = [];
  [...ix.ancestors(id), id].forEach((x) => {
    const parent = ix.parentOf(x);
    if (parent != null && ix.isChoice(parent) && ix.pickedOf(parent) !== x) {
      ops.push({ op: 'pick', choice: parent, candidate: x });
      undo.push({ op: 'pick', choice: parent, candidate: ix.pickedOf(parent) });
    }
  });
  return { ops, undo };
}

/** Deux valeurs de paramètre égales (3 et « 3 »). */
export const sameValue = (a, b) => (a != null && b != null && Number.isFinite(Number(a)) && Number.isFinite(Number(b)) ? Number(a) === Number(b) : String(a ?? '') === String(b ?? ''));

/** « 1–4 » pour des entiers qui se suivent, sinon la liste. */
export function valuesText(values = []) {
  const ints = values.every((v) => Number.isInteger(v));
  if (ints && values.length > 2 && values.every((v, i) => i === 0 || v === values[i - 1] + 1)) return `${values[0]}–${values[values.length - 1]}`;
  return values.map((v) => String(v).replace('.', ',')).join(', ');
}

/** « 1-4 », « 1 à 4 », « 2; 4; 8 » ou « 0.5, 1, 2 » → valeurs. */
export function parseValues(text) {
  const span = String(text || '').match(/^\s*(-?\d+)\s*(?:-|–|à|a|\.\.)\s*(-?\d+)\s*$/);
  if (span) {
    const [low, high] = [Number(span[1]), Number(span[2])].sort((x, y) => x - y);
    return Array.from({ length: Math.min(high - low + 1, 24) }, (_, i) => low + i);
  }
  const out = [];
  String(text || '').split(/\s*[;/,]\s*|\s+/).filter(Boolean).forEach((raw) => {
    const value = Number.isFinite(Number(raw)) ? Number(raw) : raw.trim();
    if (value !== '' && !out.some((v) => sameValue(v, value))) out.push(value);
  });
  return out.slice(0, 24);
}

/** « 3 × 4 » (les valeurs retenues d'une branche, dans l'ordre de ses paramètres) ; '' s'il en manque. */
export function pointShort(params, point) {
  if (!params.length || params.some((p) => point?.[p.key] == null)) return '';
  return params.map((p) => `${String(point[p.key]).replace('.', ',')}${params.length === 1 && p.unit ? ` ${p.unit}` : ''}`).join(' × ');
}

/** « Barrettes 3 · Capteurs par barrette 4 » */
export function pointLong(ix, point) {
  return Object.entries(point || {}).map(([k, v]) => {
    const p = ix.param(k);
    return `${p?.name || k} ${String(v).replace('.', ',')}${p?.unit ? ` ${p.unit}` : ''}`;
  }).join(' · ');
}

/** Branches à paramètres dont dépend un verrou : celle qui le porte et au-dessus, et celles d'en dessous. */
export function paramBranchesFor(ix, lockId) {
  const carrier = ix.carrier(lockId);
  if (carrier == null || ix.byId.get(carrier)?.type !== 'candidate') return [];
  return [...ix.ancestors(carrier), carrier, ...ix.descendants(carrier)].filter((id) => ix.paramsOf(id).length);
}

/** Les verrous d'une branche en un coup d'œil (pastille) : à lever, levés, infirmés. */
export function lockSummary(ix, branchId) {
  const out = { total: 0, leve: 0, bloque: 0, ouvert: 0 };
  ix.locksOf(branchId).forEach((lid) => {
    const lock = ix.byId.get(lid);
    if (!lock || lock.props.dismissed) return;
    const state = ix.lockState(lid)?.state;
    out.total += 1;
    if (state === 'leve') out.leve += 1;
    else if (state === 'bloque') out.bloque += 1;
    else out.ouvert += 1;
  });
  return out;
}

/** Une branche, ses sous-branches, leurs verrous et les artefacts qui les testent. */
export function candidateSubtree(ix, candidateId) {
  const outSet = new Set();
  [candidateId, ...ix.descendants(candidateId)].forEach((cid) => {
    outSet.add(cid);
    ix.byType.artefact.forEach((a) => { if (ix.out(a.id, 'tests').includes(cid)) outSet.add(a.id); });
    ix.locksOf(cid).forEach((lid) => { if (ix.byId.has(lid)) outSet.add(lid); });
  });
  return outSet;
}

/** Du dossier de base au verrou : [dossier, …, branche qui le porte, verrou]. */
export function lockAncestry(ix, lockId) {
  const carrier = ix.carrier(lockId);
  if (carrier == null) return [lockId];
  if (ix.byId.get(carrier)?.type !== 'candidate') return [carrier, lockId];
  return [...ix.ancestors(carrier), carrier, lockId];
}

/** Ce qu'on met en avant autour d'un élément (survol, sélection). */
export function focusSet(ix, id) {
  const node = id != null ? ix.byId.get(id) : null;
  if (!node) return null;
  const outSet = new Set([id]);
  const chain = (lid) => lockAncestry(ix, lid).forEach((x) => outSet.add(x));
  if (node.type === 'attribute') {
    ix.inn(id, 'concerns').forEach((lid) => chain(lid));
  } else if (node.type === 'candidate') {
    candidateSubtree(ix, id).forEach((x) => outSet.add(x));
    ix.ancestors(id).forEach((x) => outSet.add(x));
  } else if (node.type === 'lock') {
    chain(id);
    ix.out(id, 'concerns').forEach((a) => outSet.add(a));
  } else if (node.type === 'artefact') {
    ix.out(id, 'tests').forEach((c) => { outSet.add(c); ix.ancestors(c).forEach((x) => outSet.add(x)); });
    ix.resultsOf(id).forEach((r) => chain(r.lock_id));
  } else return null;
  return outSet;
}

/** Verrous auxquels un artefact qui teste cette branche peut répondre : les siens, puis ceux de ses sous-branches. */
export function lockOptions(ix, candidateId) {
  const node = ix.byId.get(candidateId);
  if (node?.type === 'configuration') {
    const inside = (ix.scenario(candidateId)?.candidate_ids || []).map(Number);
    return [
      ...ix.locksOf(candidateId).map((lid) => ({ lockId: lid, how: 'intégration' })),
      ...inside.flatMap((cid) => ix.locksOf(cid).map((lid) => ({ lockId: lid, how: ix.byId.get(cid)?.title || '' }))),
    ].filter((o) => ix.byId.has(o.lockId) && !ix.byId.get(o.lockId).props.dismissed);
  }
  return [candidateId, ...ix.descendants(candidateId)]
    .flatMap((cid) => ix.locksOf(cid).map((lid) => ({ lockId: lid, how: cid === candidateId ? 'porte' : `sous ${ix.byId.get(cid)?.title || ''}` })))
    .filter((o) => ix.byId.has(o.lockId) && !ix.byId.get(o.lockId).props.dismissed);
}

// ── États présentés ───────────────────────────────────────────────────────────────────────────────────────────
const SHORT = { 0.5: 'Av', 1: 'T', 2: 'S', 3: 'P' };
export const maturityShort = (m) => SHORT[m] || '';

export function attributeChip(ev) {
  switch (ev?.state) {
    case 'couvert': return { tone: 'emerald', label: `Couvert · ${maturityShort(ev.maturity)}`, check: true };
    case 'en_cours': return { tone: 'sky', label: 'En cours' };
    case 'sans_solution': return { tone: 'rose', label: 'Sans solution' };
    default: return { tone: 'slate', label: 'Sans verrou' };
  }
}

export function candidateChip(ev, node) {
  if (node?.props?.exit === 'integre') return { tone: 'emerald', label: 'Intégrée', check: true };
  if (node?.props?.exit === 'en_sommeil') return { tone: 'zinc', label: 'En sommeil' };
  switch (ev?.state) {
    case 'valide': return { tone: 'emerald', label: `Validée · ${maturityShort(ev.maturity)}`, check: true };
    case 'exploration': return { tone: 'amber', label: 'Verrous à lever' };
    case 'bloque': return { tone: 'rose', label: 'Bloquée' };
    case 'abandonne': return { tone: 'zinc', label: 'Abandonnée' };
    default: return { tone: 'slate', label: 'À qualifier' };
  }
}

export function lockChip(ev) {
  switch (ev?.state) {
    case 'leve': return { tone: 'emerald', label: `Levé · ${maturityShort(ev.maturity)}`, check: true };
    case 'bloque': return { tone: 'rose', label: 'Infirmé' };
    case 'en_cours': return { tone: 'sky', label: 'En cours' };
    case 'mitige': return { tone: 'amber', label: 'Mitigé' };
    case 'ecarte': return { tone: 'zinc', label: 'Écarté' };
    default: return { tone: 'slate', label: 'À démontrer' };
  }
}

export function configurationChip(se) {
  switch (se?.state) {
    case 'valide': return { tone: 'emerald', label: `Validée · ${maturityShort(se.maturity)}`, check: true };
    case 'en_cours': return { tone: 'sky', label: 'En cours' };
    case 'bloque': return { tone: 'rose', label: 'Bloquée' };
    case 'incomplet': return { tone: 'amber', label: 'Alternative à choisir' };
    default: return { tone: 'slate', label: 'Vide' };
  }
}

export function artefactChip(artefact, results) {
  const status = artefact?.props?.status;
  if (status === 'annule') return { tone: 'zinc', label: 'Annulé' };
  const verdicts = (results || []).map((r) => r.verdict).filter(Boolean);
  if (status === 'termine' || verdicts.length) {
    if (verdicts.includes('negatif')) return { tone: 'rose', label: 'Infirme' };
    if (verdicts.includes('mitige')) return { tone: 'amber', label: 'Mitigé' };
    if (verdicts.length && verdicts.every((v) => v === 'positif')) return { tone: 'emerald', label: 'Valide' };
  }
  return { tone: ARTEFACT_STATUS[status]?.tone || 'slate', label: ARTEFACT_STATUS[status]?.label || 'Planifié' };
}

export const CRITICALITY = {
  critique: { label: 'Critique', tone: 'rose' },
  elevee: { label: 'Élevée', tone: 'orange' },
  moyenne: { label: 'Moyenne', tone: 'amber' },
  faible: { label: 'Faible', tone: 'slate' },
  nulle: { label: 'Nulle', tone: 'zinc' },
};

export const LOCK_COLOR = {
  leve: '#10b981', mitige: '#f59e0b', bloque: '#f43f5e', en_cours: '#d97706', ouvert: '#94a3b8', ecarte: '#d4d4d8',
};
export const CANDIDATE_COLOR = {
  valide: '#10b981', exploration: '#d97706', bloque: '#f43f5e', a_qualifier: '#94a3b8', abandonne: '#d4d4d8',
};

export const actorName = (actor, employees) => {
  if (!actor) return '';
  if (actor === 'nexus') return 'Nexus';
  const id = Number(String(actor).split(':')[1]);
  return employees?.find((e) => e.id === id)?.name || actor;
};

/** L'élément visé par une opération (identifiant ou code « V4 ») ; null pour une référence du même lot. */
const nodeOf = (x, ix) => (typeof x === 'number' ? ix?.byId.get(x) : ix?.byId.get(ix?.codes.get(String(x || '').toUpperCase()))) || null;

/** Une opération qui ne changerait rien (statut déjà « terminé », réponse déjà notée) : inutile de la montrer. */
export const isNoopOperation = (op, ix) => {
  if (op?.op === 'update' && !op.title) {
    const node = nodeOf(op.id, ix);
    return !!node && Object.entries(op.props || {}).every(([k, v]) => JSON.stringify(node.props?.[k] ?? null) === JSON.stringify(v ?? null));
  }
  if (op?.op === 'result') {
    const [a, l] = [nodeOf(op.artefact, ix), nodeOf(op.lock, ix)];
    return !!(a && l) && ix.resultsOf(a.id).some((r) => r.lock_id === l.id && (r.verdict || null) === (op.verdict || null));
  }
  return false;
};

const PROP_LABELS = {
  objective: 'objectif', protocol: 'protocole', results: 'résultats', conclusion: 'conclusion', completed_at: 'terminé le',
  start_date: 'début', due_date: 'échéance', description: 'description', question: 'question', threshold: 'seuil',
  threshold_text: 'seuil', stop_criterion: 'critère d’arrêt', lock_type: 'type', target: 'cible', nature: 'nature',
  level: 'niveau de preuve', owner_id: 'responsable', parent_id: 'place dans l’arbre', children: 'sous-branches',
  picked_id: 'alternative retenue', parameters: 'paramètres', point: 'valeurs retenues',
};

export const describeOperation = (op, ix) => {
  const ref = (x) => (typeof x === 'number' ? ix?.code(x) : String(x || '').replace(/^\$/, ''));
  const titled = (x) => { const node = nodeOf(x, ix); return node ? `${node.code} « ${node.title} »` : ref(x); };
  const changes = (props) => Object.entries(props || {}).map(([k, v]) => {
    if (k === 'status' && ARTEFACT_STATUS[v]) return `statut ${ARTEFACT_STATUS[v].label.toLowerCase()}`;
    if (k === 'measurements') {
      const filled = (v || []).filter((m) => m && String(m.value ?? '').trim());
      return filled.length ? `mesures : ${filled.map((m) => `${m.label} = ${m.value}${m.unit ? ` ${m.unit}` : ''}`).join(', ')}` : 'mesures';
    }
    const label = PROP_LABELS[k] || k;
    if (typeof v === 'string' && v.trim() && !['completed_at', 'start_date', 'due_date'].includes(k)) {
      const text = v.replace(/\s+/g, ' ').trim();
      return `${label} « ${text.length > 90 ? `${text.slice(0, 90)}…` : text} »`;
    }
    return typeof v === 'string' && v ? `${label} ${v}` : label;
  }).join(' · ');
  switch (op?.op) {
    case 'create': return `Créer ${TYPES[op.type]?.label?.toLowerCase() || op.type} « ${op.title || 'sans titre'} »${op.props?.parent_id != null ? ` sous ${titled(op.props.parent_id)}` : ''}`;
    case 'update': return `Modifier ${ref(op.id)}${op.title ? ` : « ${op.title} »` : ''}${Object.keys(op.props || {}).length ? ` (${changes(op.props)})` : ''}`;
    case 'delete': return `Supprimer ${ref(op.id)}`;
    case 'link': return `Lier ${ref(op.src)} → ${ref(op.dst)} (${op.type})`;
    case 'unlink': return `Délier ${ref(op.src)} → ${ref(op.dst)}`;
    case 'result': return `${ref(op.artefact)} répond à ${titled(op.lock)}${op.params ? ` pour ${Object.entries(op.params).map(([k, v]) => `${ix?.param?.(k)?.name || k} ${v}`).join(', ')}` : ''}${op.verdict ? ` : ${VERDICTS[op.verdict]?.label}` : ''}${op.measure?.value != null ? ` (${op.measure.value}${op.measure.unit ? ` ${op.measure.unit}` : ''})` : ''}`;
    case 'alternative': return `Ajouter « ${op.title || 'sans titre'} » comme alternative à ${titled(op.of)}`;
    case 'pick': return op.candidate != null ? `Retenir ${titled(op.candidate)} pour ${titled(op.choice)}` : `Ne plus retenir d’alternative pour ${titled(op.choice)}`;
    case 'keep': return `${ref(op.configuration)} prend ${titled(op.candidate)}`;
    case 'drop': return `${ref(op.configuration)} ne prend plus ${titled(op.candidate)}`;
    case 'complete': return `Compléter ${ref(op.configuration)} (une alternative par choix)`;
    case 'decide': return `Décision proposée : « ${op.title || ''} »`;
    default: return op?.op || '?';
  }
};
